// The numbers behind the dashboard's Insights view (docs/ui_redesign/17-dashboard-insights-vendormap-as-built.md).
// Pure functions over rows the app already loads: the Invoices list (GET /api/invoicedocument/{modelDefId}), the
// Payment dashboard (GET /api/dashboard/appayments/{modelDefId}) and the dashboard's own queue rows.
import { invoiceQueue, splitVendor } from '../lists/invoiceList';
import { isRejected } from '../status';

export const DAY = 86400000;
export const PERIODS = [1, 2, 4, 9, 26, 52];
export const DEFAULT_WEEKS = 9;
// The Invoices list call takes one of the app's week filters. To compare with the previous period it needs twice the
// period, so it asks for the next filter that covers it (0 = no filter).
export const FETCH_WEEKS = { 1: 2, 2: 4, 4: 9, 9: 26, 26: 52, 52: 0 };
export const periodName = w => (w === 1 ? 'Last week' : `Last ${w} weeks`);

const ms = d => { if (!d) return null; const x = new Date(d).getTime(); return Number.isFinite(x) ? x : null; };
const sid = r => Number(r && r.invoiceDocumentStatusId);
const num = v => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const startOfDay = t => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };

export const OPEN_QUEUES = ['unmatched', 'correct', 'approve', 'ready', 'failed'];
export const QUEUE_LABELS = { scan: 'Waiting to scan', unmatched: 'Unmatched', correct: 'Needs correction', approve: 'Pending approval', ready: 'Ready to post', failed: 'Post failed' };

// Why an open invoice needs a fix, by its status. `open` says where the chart row leads.
export const EXCEPTIONS = [
	{ key: 'vendor', label: 'Vendor not found', test: s => s > 0 && s < 40, open: { queue: 'unmatched' } },
	{ key: 'missing', label: 'Missing info', test: s => s === 40, open: { queue: 'correct', chip: 's40' } },
	{ key: 'errors', label: 'Verified with errors', test: s => s === 60, open: { queue: 'correct', chip: 's60' } },
	{ key: 'amount', label: 'Amount exceeded', test: s => s === 41, open: { invoices: 'correct' } }, // the dashboard queues never list 41
	{ key: 'rejected', label: 'Rejected', test: s => isRejected(s), open: { queue: 'approve', chip: 'rejected' } },
	{ key: 'failed', label: 'Post failed', test: s => s === 180, open: { queue: 'failed' } }
];

export const AGE_BUCKETS = [
	{ key: 'a7', label: '0–7 d', max: 7 },
	{ key: 'a14', label: '8–14 d', max: 14 },
	{ key: 'a30', label: '15–30 d', max: 30 },
	{ key: 'aOld', label: 'Over 30 d', max: Infinity }
];

// The chart's time buckets: days for up to two weeks, weeks up to nine, then months.
export function timeBuckets(weeks, now = Date.now()) {
	const end = now;
	const start = end - weeks * 7 * DAY;
	const fmt = (t, o) => new Date(t).toLocaleDateString('en-US', o);
	if (weeks <= 2) {
		const first = startOfDay(end) - (weeks * 7 - 1) * DAY;
		return Array.from({ length: weeks * 7 }, (_, i) => ({ from: first + i * DAY, to: first + (i + 1) * DAY, label: fmt(first + i * DAY, { month: 'short', day: 'numeric' }) }));
	}
	if (weeks <= 9) {
		return Array.from({ length: weeks }, (_, i) => ({ from: start + i * 7 * DAY, to: start + (i + 1) * 7 * DAY, label: fmt(start + i * 7 * DAY, { month: 'short', day: 'numeric' }) }));
	}
	const out = [];
	const d = new Date(start);
	let m = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
	while (m <= end) {
		const n = new Date(new Date(m).getFullYear(), new Date(m).getMonth() + 1, 1).getTime();
		out.push({ from: Math.max(m, start), to: Math.min(n, end + 1), label: fmt(m, { month: 'short' }) });
		m = n;
	}
	return out;
}
export const bucketUnit = weeks => (weeks <= 2 ? 'Day' : weeks <= 9 ? 'Week starting' : 'Month');

const count = (list, times, b) => list.filter((_, i) => times[i] != null && times[i] >= b.from && times[i] < b.to).length;

// Per-currency totals: [[currency, amount], ...], largest first.
export function byCurrency(rows, amountOf) {
	const m = new Map();
	rows.forEach(r => { const c = r.currencyCode || ''; m.set(c, (m.get(c) || 0) + num(amountOf(r))); });
	return [...m.entries()].filter(([c]) => c).sort((a, b) => b[1] - a[1]);
}

export const change = (now, before) => (before > 0 ? Math.round(((now - before) / before) * 100) : null);

// approvers: the Pending approval queue rows' assignedTo (names, as the gadget shows them).
export function approverCounts(pending) {
	const m = new Map();
	(pending || []).forEach(r => {
		if (isRejected(sid(r))) return;
		String(r.assignedTo || '').split(/[,;|]/).map(s => s.trim()).filter(Boolean).forEach(n => m.set(n, (m.get(n) || 0) + 1));
	});
	return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

// Approval level an invoice waits for: 50 waits for level 1, 70 for level 2, and so on.
export const waitingLevel = s => (s === 50 || (s >= 70 && s <= 150 && (s - 50) % 20 === 0) ? (s - 50) / 20 + 1 : null);

const paidStatus = p => {
	const s = String(p.paymentStatus || '').toLowerCase();
	return s.startsWith('fully') ? 'full' : s.startsWith('partial') ? 'partial' : 'unpaid';
};

export function insights({ invoices = [], payments = [], queued = [], pending = [], weeks = DEFAULT_WEEKS, now = Date.now() } = {}) {
	const start = now - weeks * 7 * DAY;
	const prevStart = start - weeks * 7 * DAY;
	const created = invoices.map(r => ms(r.dateUpdated));
	const inPeriod = invoices.filter((_, i) => created[i] != null && created[i] >= start && created[i] <= now);
	const inPrev = invoices.filter((_, i) => created[i] != null && created[i] >= prevStart && created[i] < start);

	const openRows = invoices.filter(r => OPEN_QUEUES.includes(invoiceQueue(r)));
	const queues = { scan: (queued || []).length };
	OPEN_QUEUES.forEach(q => { queues[q] = 0; });
	openRows.forEach(r => { queues[invoiceQueue(r)]++; });

	const exceptions = EXCEPTIONS.map(e => ({ ...e, count: openRows.filter(r => e.test(sid(r))).length }));
	const needFix = exceptions.reduce((a, e) => a + e.count, 0);

	const ages = openRows.map(r => ms(r.dateUpdated)).filter(t => t != null).map(t => Math.max(0, Math.floor((now - t) / DAY)));
	const aging = AGE_BUCKETS.map((b, i) => ({ ...b, count: ages.filter(a => a <= b.max && (i === 0 || a > AGE_BUCKETS[i - 1].max)).length }));
	const oldest = ages.length ? Math.max(...ages) : null;

	const today = startOfDay(now);
	const dues = openRows.map(r => ms(r.dueDate)).filter(t => t != null);
	const pastDue = dues.filter(t => t < today).length;
	const dueSoon = dues.filter(t => t >= today && t < today + 8 * DAY).length;

	const levels = new Map();
	openRows.filter(r => invoiceQueue(r) === 'approve').forEach(r => { const l = waitingLevel(sid(r)); if (l) levels.set(l, (levels.get(l) || 0) + 1); });

	// Posted: the Invoices list's Posted (170) invoices. The list has no posting date, so an invoice counts in the
	// period its last change (modifyDate, else created) falls in; the list's own week filter uses the same dates.
	const postedAt = r => ms(r.modifyDate) != null ? ms(r.modifyDate) : ms(r.dateUpdated);
	const postedRows = invoices.filter(r => sid(r) === 170);
	const posted = postedRows.map(postedAt);
	const postedNow = postedRows.filter((_, i) => posted[i] != null && posted[i] >= start && posted[i] <= now);
	const postedPrev = postedRows.filter((_, i) => posted[i] != null && posted[i] >= prevStart && posted[i] < start);

	// Payment status: the Payment dashboard's rows, by their posted date.
	const paidAt = payments.map(p => ms(p.postedDate));
	const paidNow = payments.filter((_, i) => paidAt[i] != null && paidAt[i] >= start && paidAt[i] <= now);
	const pay = { full: [], partial: [], unpaid: [] };
	paidNow.forEach(p => pay[paidStatus(p)].push(p));
	const paidPct = paidNow.length ? Math.round((pay.full.length / paidNow.length) * 100) : null;

	const vendors = new Map(); // currency -> Map(vendor -> total amount of its posted invoices)
	postedNow.forEach(r => {
		const c = r.currencyCode || '';
		if (!c) return;
		const name = splitVendor(r.vendor).name || r.vendor || r.vendorName || '';
		if (!vendors.has(c)) vendors.set(c, new Map());
		const v = vendors.get(c);
		v.set(name, (v.get(name) || 0) + num(r.totalAmount));
	});
	const topVendors = [...vendors.entries()]
		.map(([c, v]) => ({ currency: c, total: [...v.values()].reduce((a, b) => a + b, 0), rows: [...v.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5) }))
		.sort((a, b) => b.total - a.total);

	const buckets = timeBuckets(weeks, now);
	const createdNow = inPeriod.map(r => ms(r.dateUpdated));
	const postedTimes = postedNow.map(postedAt);

	return {
		weeks,
		received: { count: inPeriod.length, prev: inPrev.length, change: change(inPeriod.length, inPrev.length), series: buckets.map(b => count(inPeriod, createdNow, b)) },
		posted: { count: postedNow.length, prev: postedPrev.length, change: change(postedNow.length, postedPrev.length), amounts: byCurrency(postedNow, r => r.totalAmount), series: buckets.map(b => count(postedNow, postedTimes, b)) },
		buckets,
		open: { count: openRows.length + queues.scan, needFix, queues },
		exceptions,
		aging,
		oldest,
		over14: ages.filter(a => a > 14).length,
		pastDue,
		dueSoon,
		levels: [...levels.entries()].sort((a, b) => a[0] - b[0]),
		approvers: approverCounts(pending).slice(0, 5),
		payments: {
			count: paidNow.length,
			paidPct,
			parts: ['full', 'partial', 'unpaid'].map(k => ({ key: k, count: pay[k].length, amounts: byCurrency(pay[k], p => p.amount) }))
		},
		topVendors
	};
}
